import { Mesh, Web3DViewer } from '@technology/web3d';
import {
  InstancedMesh,
  Intersection,
  Object3D,
  Raycaster,
  ThreejsModel,
  ThreejsPlugin,
  Vector2,
  Vector3,
} from '@technology/web3d-plugin-threejs';
import { AlignmentNavigationTool } from '@technology/web3d-plugin-trimbim/dist/esnext/Tools/AlignmentNavigationTool';
import { screenPositionToRayTracePoint } from '@technology/web3d/dist/esnext/Picker/Picker';

const MESH_COUNT_OPTIMIZATION_THRESHOLD = 100; // Enable distance culling above this count
const MAX_HOVER_DISTANCE = 10000; // Maximum distance for hover detection (scene units)
const DIRECTION_Z = 0.5;
const CLICK_PIXEL_DISTANCE_THRESHOLD = 5;

export interface PickResult {
  meshId: number;
  instanceId?: number;
}

export class ThreeJsManager {
  private threeJsModel: ThreejsModel | undefined;
  private listeners: (() => void)[] = [];

  private hoverListeners: ((result: PickResult | null) => void)[] = [];
  private pickListeners: ((result: PickResult) => void)[] = [];
  private readonly meshes: Map<number, Object3D> = new Map();
  private mouseDownPosition: { x: number; y: number } | null = null;
  private hasMouseMoveListener = false;
  private hasMouseClickListeners = false;

  private readonly filterCtrlKeyMouseEvent = (): boolean => {
    return !!(
      this.viewer.tools['alignmentNavigation'] as AlignmentNavigationTool
    )?.pickingActive;
  };

  private readonly handleViewerMouseDown = (event: MouseEvent): void => {
    if (this.filterCtrlKeyMouseEvent()) {
      return;
    }
    this.mouseDownPosition = {
      x: event.clientX,
      y: event.clientY,
    };
  };

  private readonly handleViewerMouseMove = (event: MouseEvent): void => {
    if (this.filterCtrlKeyMouseEvent() || !this.threeJsModel) {
      return;
    }

    const result = this.getPickResult(
      new Vector2(event.clientX, event.clientY)
    );

    this.hoverListeners.forEach((listener) => {
      listener(result);
    });
  };

  private readonly handleViewerMouseUp = (event: MouseEvent): void => {
    if (this.filterCtrlKeyMouseEvent() || !this.threeJsModel) {
      return;
    }

    // Check if this is a true click (minimal movement) or a drag release
    if (this.mouseDownPosition) {
      const deltaX = event.clientX - this.mouseDownPosition.x;
      const deltaY = event.clientY - this.mouseDownPosition.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (distance > CLICK_PIXEL_DISTANCE_THRESHOLD) {
        this.mouseDownPosition = null;
        return;
      }
    }

    this.mouseDownPosition = null;

    const result = this.getPickResult(
      new Vector2(event.clientX, event.clientY)
    );

    if (result === null) {
      return;
    }

    this.pickListeners.forEach((listener) => {
      listener(result);
    });

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private readonly handleSelection = (): void => {
    const selection = this.viewer.selection.get('threejs');
    if (!selection || selection.length === 0) {
      return;
    }

    // Find any of our managed meshes that got selected and remove them
    // This prevents Web3D's built-in selection system from highlighting our custom meshes
    const meshIdsToRemove = selection.filter((id) => this.meshes.has(id));

    if (meshIdsToRemove.length > 0) {
      this.viewer.selection.subtract('threejs', meshIdsToRemove);
    }
  };

  constructor(
    private readonly viewer: Web3DViewer,
    private readonly threejsPlugin: ThreejsPlugin
  ) {}

  public async cleanup(): Promise<void> {
    this.threeJsModel?.clear();
    await this.disposeThreeJsModel();
  }

  public createThreeJsModel(): void {
    if (this.threeJsModel) {
      return;
    }

    this.threeJsModel = this.threejsPlugin.createModel();

    this.listeners.forEach((listener) => {
      this.threejsPlugin.addAnimationFrameListener(listener);
    });

    if (this.hoverListeners.length > 0 && !this.hasMouseMoveListener) {
      this.viewer.addEventListener('mousemove', this.handleViewerMouseMove);
      this.hasMouseMoveListener = true;
    }

    if (this.pickListeners.length > 0 && !this.hasMouseClickListeners) {
      this.viewer.addEventListener('mousedown', this.handleViewerMouseDown);
      this.viewer.addEventListener('mouseup', this.handleViewerMouseUp);
      this.hasMouseClickListeners = true;
    }

    this.viewer.selection.subscribe(this.handleSelection);
  }

  public async disposeThreeJsModel(): Promise<void> {
    if (!this.threeJsModel) return;

    if (this.viewer.getModel(this.threeJsModel.modelId)) {
      await this.viewer.unload(this.threeJsModel.modelId, {
        fitToView: false,
      });
    }

    if (this.threeJsModel) {
      this.threeJsModel.dispose();
      this.threeJsModel = undefined;
    }

    this.meshes.clear();

    if (this.hasMouseMoveListener) {
      this.viewer.removeEventListener('mousemove', this.handleViewerMouseMove);
      this.hasMouseMoveListener = false;
    }

    if (this.hasMouseClickListeners) {
      this.viewer.removeEventListener('mousedown', this.handleViewerMouseDown);
      this.viewer.removeEventListener('mouseup', this.handleViewerMouseUp);
      this.mouseDownPosition = null;
      this.hasMouseClickListeners = false;
    }

    this.viewer.selection.unsubscribe(this.handleSelection);
  }

  public addAnimationFrameListener(listener: () => void): void {
    if (this.listeners.includes(listener)) {
      return;
    }

    this.createThreeJsModel();
    this.listeners.push(listener);
    this.threejsPlugin.addAnimationFrameListener(listener);
  }

  public async removeAnimationFrameListener(
    listener: () => void
  ): Promise<void> {
    this.listeners = this.listeners.filter((l) => l !== listener);
    this.threejsPlugin.removeAnimationFrameListener(listener);
    await this.disposeModelIfEmpty();
  }

  public addObject(...meshes: Mesh[]): void {
    this.createThreeJsModel();
    this.threeJsModel?.add(...meshes);
    this.threeJsModel?.refreshBoundingBox();

    meshes.forEach((mesh) => {
      this.meshes.set(mesh.id, mesh);
    });
  }

  public async removeObject(...meshes: Mesh[]): Promise<void> {
    this.threeJsModel?.remove(...meshes);
    this.threeJsModel?.refreshBoundingBox();

    meshes.forEach((mesh) => {
      this.meshes.delete(mesh.id);
    });

    await this.disposeModelIfEmpty();
  }

  public forceRefresh(): void {
    this.threeJsModel?.refreshBoundingBox();
  }

  public redraw(): void {
    this.threejsPlugin.redraw();
  }

  public addHoverListener(listener: (result: PickResult | null) => void): void {
    if (this.hoverListeners.includes(listener)) {
      return;
    }

    this.hoverListeners.push(listener);

    if (!this.hasMouseMoveListener) {
      this.viewer.addEventListener('mousemove', this.handleViewerMouseMove);
      this.hasMouseMoveListener = true;
    }
  }

  public removeHoverListener(
    listener: (result: PickResult | null) => void
  ): void {
    this.hoverListeners = this.hoverListeners.filter((l) => l !== listener);

    if (this.hoverListeners.length === 0 && this.hasMouseMoveListener) {
      this.viewer.removeEventListener('mousemove', this.handleViewerMouseMove);
      this.hasMouseMoveListener = false;
    }
  }

  public addPickListener(listener: (result: PickResult) => void): void {
    if (this.pickListeners.includes(listener)) {
      return;
    }

    this.pickListeners.push(listener);

    if (!this.hasMouseClickListeners) {
      this.viewer.addEventListener('mousedown', this.handleViewerMouseDown);
      this.viewer.addEventListener('mouseup', this.handleViewerMouseUp);
      this.hasMouseClickListeners = true;
    }
  }

  public removePickListener(listener: (result: PickResult) => void): void {
    this.pickListeners = this.pickListeners.filter((l) => l !== listener);

    if (this.pickListeners.length === 0 && this.hasMouseClickListeners) {
      this.viewer.removeEventListener('mousedown', this.handleViewerMouseDown);
      this.viewer.removeEventListener('mouseup', this.handleViewerMouseUp);
      this.mouseDownPosition = null;
      this.hasMouseClickListeners = false;
    }
  }

  private async disposeModelIfEmpty(): Promise<void> {
    if (
      this.threeJsModel &&
      this.listeners.length === 0 &&
      this.threeJsModel.root.children.length === 0
    ) {
      await this.disposeThreeJsModel();
    }
  }

  private createCaster(screenPosition: Vector2): Raycaster {
    const container = this.viewer as HTMLElement;

    const rayTracePoint = screenPositionToRayTracePoint(
      screenPosition,
      container
    );

    const direction = new Vector3(
      rayTracePoint.x,
      rayTracePoint.y,
      DIRECTION_Z
    );

    const invertedProjection = this.viewer.camera.projectionMatrix
      .clone()
      .invert();

    direction.applyMatrix4(invertedProjection);
    direction.applyMatrix4(this.viewer.camera.matrixWorld);
    direction.sub(this.viewer.camera.position).normalize();

    const raycaster = new Raycaster();
    raycaster.set(this.viewer.camera.position, direction);

    return raycaster;
  }

  private pickMesh(raycaster: Raycaster): Intersection | null {
    if (this.meshes.size === 0) {
      return null;
    }

    const meshesToCheck =
      this.meshes.size > MESH_COUNT_OPTIMIZATION_THRESHOLD
        ? this.filterMeshesByDistance()
        : Array.from(this.meshes.values());

    if (meshesToCheck.length === 0) {
      return null;
    }

    const intersections = raycaster.intersectObjects(meshesToCheck, true);
    return intersections.length > 0 ? intersections[0] : null;
  }

  private filterMeshesByDistance(): Object3D[] {
    const cameraPos = this.viewer.camera.position;
    const maxDistanceSquared = MAX_HOVER_DISTANCE * MAX_HOVER_DISTANCE;

    return Array.from(this.meshes.values()).filter((mesh) => {
      if (!mesh.visible) return false;

      // Always include InstancedMesh - its position doesn't represent instance positions,
      // and Three.js raycast handles per-instance intersection internally
      if (mesh instanceof InstancedMesh) return true;

      const meshPos = (mesh as Mesh).position;
      const dx = meshPos.x - cameraPos.x;
      const dy = meshPos.y - cameraPos.y;
      const dz = meshPos.z - cameraPos.z;
      const distanceSquared = dx * dx + dy * dy + dz * dz;

      return distanceSquared < maxDistanceSquared;
    });
  }

  /**
   * Get the pick result (mesh ID and optional instance ID) at the given screen position
   * @param screenPosition - The absolute screen position to check
   * @returns PickResult with meshId and optional instanceId, or null if nothing was picked
   */
  private getPickResult(screenPosition: Vector2): PickResult | null {
    const caster = this.createCaster(screenPosition);
    const intersection = this.pickMesh(caster);

    if (!intersection) {
      return null;
    }

    const meshId = intersection.object.id;
    const instanceId =
      'instanceId' in intersection ? intersection.instanceId : undefined;

    return { meshId, instanceId };
  }
}
